import React, { useState, useEffect } from 'react';
import SetPassword from './SetPassword';
import { GuhinduraUmuyoboziAkagari } from './GuhinduraUmuyobozi';
import {
  Container,
  Card,
  Nav,
  Navbar,
  Button,
  Row,
  Col,
  ProgressBar,
} from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import Calendar from 'react-calendar';
import axios from "../utils/axiosInstance";
import 'react-calendar/dist/Calendar.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

// API BASE URL
const API_URL = "";

// Axios instance with auth token (checks both token keys used by different login flows)
const authAxios = axios.create({ baseURL: API_URL });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || localStorage.getItem('superToken');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

const cyberTheme = {
  bg: "#0a0e17",
  card: "rgba(15, 23, 42, 0.82)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  primary: "#6366f1",
  accent: "#a855f7",
  glow: "0 0 25px rgba(99, 102, 241, 0.35)",
  borderGlow: "1px solid rgba(99, 102, 241, 0.2)",
  inputBg: "rgba(30, 41, 59, 0.85)",
  buttonGradient: "linear-gradient(135deg, #6366f1, #a855f7)",
  labelColor: "#e2e8f0",
  success: "#10b981",
  danger: "#ef4444",
  darkRow: "rgba(30, 41, 59, 0.7)",
  darkRowHover: "rgba(45, 55, 75, 0.9)",
};

function SuperAdminDashboard() {
  const [activeKey, setActiveKey] = useState('dashboard');
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // State populated by Backend
  const [users, setUsers] = useState({
    akagari: [],
    umudugudu: [],
    abandi: [],
  });

  // Fetch Data when tab changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        let endpoint = "";
        let category = "";

        // Determine which endpoint to call based on the tab
        if (activeKey === 'akagari') {
          endpoint = "/get_akagari";
          category = "akagari";
        } else if (activeKey === 'umudugudu') {
          endpoint = "/get_mudugudu";
          category = "umudugudu";
        } else if (activeKey === 'abandi') {
          endpoint = "/get_abandi";
          category = "abandi";
        }

        // Execute Fetch
        if (endpoint) {
          const res = await authAxios.get(`${API_URL}${endpoint}`);
          
          // Map Database Columns (snake_case) to UI Display (camelCase)
          const mappedData = res.data.map(user => ({
            id: user.user_id,             // DB: user_id
            userId: user.user_id,         // DB: user_id
            supId: user.sup_id || '-',    // DB: sup_id (only for Mudugudu/Abandi)
            nationalId: user.national_id, // DB: national_id
            fullName: user.fullname,      // DB: fullname
            email: user.email,            // DB: email
            phone: user.telefone,         // DB: telefone
            role: user.role || 'N/A',     // DB: role (only for Abandi)
            createdAt: new Date(user.created_at).toLocaleString(), // DB: created_at
            status: "Active"              // Placeholder (not in DB yet)
          }));

          setUsers(prev => ({ ...prev, [category]: mappedData }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [activeKey]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) setShowMobileSidebar(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavChange = (key) => {
    setActiveKey(key);
    setShowMobileSidebar(false);
  };

  // Toggle Status Function (Optimistic UI)
  const toggleStatus = (category, userId) => {
    setUsers(prev => {
      const updated = { ...prev };
      updated[category] = updated[category].map(user =>
        user.userId === userId
          ? { ...user, status: user.status === "Active" ? "Inactive" : "Active" }
          : user
      );
      return updated;
    });
  };

  // Modern Table Component
  const ModernTable = ({ title, description, headers, rows, category }) => (
    <Card className="mb-5 shadow-lg" style={{
      background: cyberTheme.card,
      border: cyberTheme.borderGlow,
      borderRadius: '16px',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
    }}>
      <Card.Body className="p-4">
        <h4 style={{ color: cyberTheme.primary, fontWeight: 600, marginBottom: '0.5rem' }}>
          {title}
        </h4>
        <p className="text-muted mb-4" style={{ fontSize: '0.95rem' }}>
          {description}
        </p>
        <div className="table-responsive">
          <table style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '0 10px',
            color: cyberTheme.text,
          }}>
            <thead>
              <tr style={{
                background: 'rgba(30, 41, 59, 0.9)',
                borderBottom: `2px solid ${cyberTheme.primary}40`,
              }}>
                {headers.map((h, i) => (
                  <th key={i} style={{
                    padding: '14px 16px',
                    textTransform: 'uppercase',
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    color: cyberTheme.muted,
                    letterSpacing: '0.4px',
                    border: 'none',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <tr key={row.id} style={{
                    background: cyberTheme.darkRow,
                    transition: 'all 0.22s ease',
                    borderRadius: '10px',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = cyberTheme.darkRowHover}
                    onMouseLeave={e => e.currentTarget.style.background = cyberTheme.darkRow}
                  >
                    <td style={{ padding: '14px 16px', borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}>
                      {row.id}
                    </td>
                    {/* User ID */}
                    <td style={{ padding: '14px 16px' }}>{row.userId}</td>
                    
                    {/* Sup ID (Hidden for Akagari) */}
                    {category !== 'akagari' && (
                      <td style={{ padding: '14px 16px', color: cyberTheme.accent }}>{row.supId}</td>
                    )}
                    
                    <td style={{ padding: '14px 16px' }}>{row.nationalId}</td>
                    <td style={{ padding: '14px 16px' }}>{row.fullName}</td>
                    <td style={{ padding: '14px 16px' }}>{row.email}</td>
                    <td style={{ padding: '14px 16px' }}>{row.phone}</td>
                    
                    {/* Role (Only for Abandi) */}
                    {category === 'abandi' && (
                      <td style={{ padding: '14px 16px' }}>{row.role}</td>
                    )}
                    
                    <td style={{ padding: '14px 16px', fontSize: '0.85rem' }}>{row.createdAt}</td>
                    
                    <td style={{ padding: '14px 16px' }}>
                      <span className="badge px-3 py-2 fs-6 fw-medium rounded-pill"
                        style={{
                          backgroundColor: row.status === 'Active' ? cyberTheme.success : cyberTheme.danger,
                          color: 'white',
                        }}>
                        {row.status}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }}>
                      <Button
                        size="sm"
                        className="me-2 px-3"
                        style={{
                          background: row.status === 'Active' ? cyberTheme.danger : cyberTheme.success,
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 500,
                          transition: 'all 0.2s',
                        }}
                        onClick={() => toggleStatus(category, row.userId)}
                      >
                        {row.status === 'Active' ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button variant="outline-danger" size="sm" className="px-3" style={{ borderRadius: '8px' }}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-5 text-muted">
                    No records found in database.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card.Body>
    </Card>
  );

  // ────────────────────────────────────────────────
  // Dashboard Overview
  // ────────────────────────────────────────────────
  const DashboardView = () => {
    const [calendarDate, setCalendarDate] = useState(new Date());

    const analysisData = [
      {
        title: "Mudugudu",
        usage: 85,
        users: users.umudugudu.length || "0",
        trend: "+12%",
        desc: "Active Villages",
        icon: "bi-houses",
        color: cyberTheme.primary
      },
      {
        title: "Akagari",
        usage: 62,
        users: users.akagari.length || "0",
        trend: "+5%",
        desc: "Cell Administrators",
        icon: "bi-building",
        color: cyberTheme.accent
      },
      {
        title: "Abandi",
        usage: 40,
        users: users.abandi.length || "0",
        trend: "-2%",
        desc: "Support Roles",
        icon: "bi-people",
        color: cyberTheme.success
      }
    ];

    return (
      <>
        <Card className="mb-4 shadow-lg" style={{
          background: cyberTheme.card,
          border: cyberTheme.borderGlow,
          borderRadius: '16px',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}>
          <Card.Body className="p-4">
            <h4 style={{ color: cyberTheme.primary, fontWeight: 600, marginBottom: '1.2rem' }}>
              System Activity Calendar
            </h4>
            <Row>
              <Col lg={8}>
                <div className="mb-4">
                  <Calendar
                    onChange={setCalendarDate}
                    value={calendarDate}
                    className="cyber-calendar w-100"
                    tileClassName={({ date, view }) => {
                      if (view === 'month' && date.toDateString() === new Date().toDateString()) {
                        return 'today-tile';
                      }
                      return null;
                    }}
                  />
                </div>
              </Col>
              <Col lg={4}>
                <Card style={{ background: 'rgba(30,41,59,0.6)', border: 'none', borderRadius: '12px' }}>
                  <Card.Body>
                    <h5 style={{ color: cyberTheme.accent }}>Live Statistics</h5>
                    <hr className="border-secondary" />
                    <ul className="list-unstyled">
                      <li className="mb-3 d-flex justify-content-between">
                        <span>Total Akagari:</span> 
                        <strong className="text-info">{users.akagari.length}</strong>
                      </li>
                      <li className="mb-3 d-flex justify-content-between">
                        <span>Total Mudugudu:</span> 
                        <strong className="text-warning">{users.umudugudu.length}</strong>
                      </li>
                      <li className="mb-3 d-flex justify-content-between">
                        <span>Total Abandi:</span> 
                        <strong className="text-success">{users.abandi.length}</strong>
                      </li>
                      <li className="mt-4 pt-3 border-top border-secondary">
                        System Status: <strong className="text-success">Online</strong>
                      </li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Usage Analysis Cards */}
        <h5 className="mb-3" style={{ color: cyberTheme.text, borderLeft: `4px solid ${cyberTheme.primary}`, paddingLeft: '1rem' }}>
          Database Overview
        </h5>
        <Row className="g-4 mb-5">
          {analysisData.map((item, index) => (
            <Col md={4} key={index}>
              <Card style={{
                background: cyberTheme.card,
                border: '1px solid rgba(148, 163, 184, 0.1)',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
              }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div style={{
                      width: '45px', height: '45px',
                      background: `${item.color}20`,
                      borderRadius: '10px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: item.color, fontSize: '1.5rem'
                    }}>
                      <i className={`bi ${item.icon}`}></i>
                    </div>
                    <span className={`badge ${item.trend.includes('+') ? 'bg-success' : 'bg-danger'} bg-opacity-25 text-white`}>
                      {item.trend}
                    </span>
                  </div>
                  
                  <h5 className="mb-1" style={{ color: cyberTheme.text }}>{item.title}</h5>
                  <div className="d-flex align-items-baseline mb-2">
                    <h3 className="mb-0 me-2" style={{ fontWeight: 700, color: item.color }}>{item.users}</h3>
                    <small className="text-muted">records</small>
                  </div>
                  
                  <div className="mb-2">
                    <div className="d-flex justify-content-between small text-muted mb-1">
                      <span>Capacity</span>
                      <span>{item.usage}%</span>
                    </div>
                    <ProgressBar 
                      now={item.usage} 
                      variant={item.usage > 70 ? "info" : item.usage > 50 ? "warning" : "danger"} 
                      style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.1)' }} 
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </>
    );
  };

  const renderContent = () => {
    switch (activeKey) {
      case 'dashboard':
        return <DashboardView />;
      case 'akagari':
        return (
          <ModernTable
            title="Akagari List"
            description="Administrators for Cells"
            headers={["#", "User ID", "National ID", "Full Name", "Email", "Phone", "Created At", "Status", "Actions"]}
            rows={users.akagari}
            category="akagari"
          />
        );
      case 'umudugudu':
        return (
          <ModernTable
            title="Mudugudu List"
            description="Administrators for Villages (Includes Supervisor ID)"
            headers={["#", "User ID", "Sup ID", "National ID", "Full Name", "Email", "Phone", "Created At", "Status", "Actions"]}
            rows={users.umudugudu}
            category="umudugudu"
          />
        );
      case 'abandi':
        return (
          <ModernTable
            title="Abandi List"
            description="Other Roles (Isibo, Umunyamabanga)"
            headers={["#", "User ID", "Sup ID", "National ID", "Full Name", "Email", "Phone", "Role", "Created At", "Status", "Actions"]}
            rows={users.abandi}
            category="abandi"
          />
        );
      case 'setPassword':
        return <SetPassword />;
      case 'guhinduraUmuyobozi':
        return <GuhinduraUmuyoboziAkagari />;
      case 'register':
        return <RegisterAkagari />;
      default:
        return <p className="text-center text-muted py-5">Select a category from the sidebar</p>;
    }
  };

  // ────────────────────────────────────────────────
  // REGISTER COMPONENT
  // ────────────────────────────────────────────────
  const RegisterAkagari = () => {
    const [formData, setFormData] = useState({
      national_id: "", fullname: "", email: "", telefone: "", password: "", confirmPassword: "",
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [successMessage, setSuccessMessage] = useState(false);
    const [successName, setSuccessName] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const validateForm = () => {
      const errs = {};
      if (formData.national_id.length !== 16) errs.national_id = "Indangamuntu 16 cyangwa";
      if (!formData.fullname.trim()) errs.fullname = "Amazina yose arakenewe";
      if (!formData.email.includes('@')) errs.email = "Imeyili si nziza";
      if (!/^07[0-9]{8}$/.test(formData.telefone)) errs.telefone = "Telefone si yo (07xxxxxxxx)";
      if (formData.password.length < 8) errs.password = "Ijambo rigomba kuba ≥ 8";
      if (formData.password !== formData.confirmPassword) errs.confirmPassword = "Amajwi ntabwo ahuye";
      setErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!validateForm()) return;
      setIsLoading(true);
      setErrorMessage("");

      try {
        const response = await authAxios.post(`${API_URL}/register_akagari`, formData);
        setSuccessName(formData.fullname);
        setSuccessMessage(true);
        setFormData({ national_id: "", fullname: "", email: "", telefone: "", password: "", confirmPassword: "" });
        setTimeout(() => {
          setSuccessMessage(false);
          setActiveKey('akagari');
        }, 4500);
      } catch (error) {
        const errorMsg = error.response?.data?.error || "Habaye ikosa mu kubika";
        setErrorMessage(errorMsg);
        setTimeout(() => setErrorMessage(""), 6000);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <>
        <style jsx global>{`
          .cyber-bg-glow {
            position: fixed; inset: 0; z-index: -2; pointer-events: none;
            background: radial-gradient(circle at 15% 40%, rgba(0,249,255,0.12) 0%, transparent 60%),
                        radial-gradient(circle at 85% 60%, rgba(168,85,247,0.08) 0%, transparent 70%);
          }
          .cyber-card {
            background: ${cyberTheme.card};
            backdrop-filter: blur(22px);
            border: ${cyberTheme.borderGlow};
            border-radius: 24px;
            box-shadow: ${cyberTheme.glow}, 0 20px 60px rgba(0,0,0,0.6);
            padding: 2.8rem;
            max-width: 720px;
            margin: 2rem auto;
          }
          .cyber-title {
            font-size: 3.6rem;
            font-weight: 900;
            background: linear-gradient(90deg, #6366f1, #a855f7, #6366f1);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            text-shadow: 0 0 40px rgba(0,249,255,0.6);
            text-align: center;
            margin-bottom: 2rem;
          }
          .input-cyber {
            background: ${cyberTheme.inputBg};
            border: 1px solid rgba(0,249,255,0.22);
            border-radius: 10px;
            color: white;
            padding: 0.8rem 1rem 0.8rem 3.3rem;
          }
          .input-cyber:focus {
            border-color: ${cyberTheme.primary};
            box-shadow: 0 0 0 4px rgba(0,249,255,0.18);
          }
          .cyber-icon { position: absolute; left: 1.2rem; top: 42px; color: ${cyberTheme.muted}; font-size: 1.25rem; pointer-events: none; }
          .label-cyber { color: ${cyberTheme.labelColor}; font-size: 0.9rem; font-weight: 500; margin-bottom: 0.4rem; }
          .btn-cyber {
            background: ${cyberTheme.buttonGradient};
            border: none;
            color: white;
            font-weight: 700;
            padding: 1rem;
            border-radius: 10px;
            box-shadow: 0 0 25px rgba(0,249,255,0.4);
            width: 100%;
            margin-top: 1.5rem;
          }
          .error-cyber { color: #ff6b6b; font-size: 0.85rem; margin-top: 0.3rem; }

          /* ── Success Overlay ── */
          .reg-success-overlay {
            position: fixed;
            top: 0; left: 0;
            width: 100vw; height: 100vh;
            background: rgba(0, 0, 0, 0.78);
            backdrop-filter: blur(14px);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 99999;
          }

          .reg-success-card {
            background: linear-gradient(145deg, rgba(15, 23, 42, 0.96), rgba(30, 41, 59, 0.96));
            border: 2px solid rgba(16, 185, 129, 0.5);
            border-radius: 28px;
            padding: 3rem 3.5rem;
            text-align: center;
            max-width: 500px;
            width: 92%;
            box-shadow:
              0 0 80px rgba(16, 185, 129, 0.25),
              0 0 160px rgba(16, 185, 129, 0.08),
              0 30px 70px rgba(0, 0, 0, 0.55);
            position: relative;
            overflow: hidden;
          }

          .reg-success-card::before {
            content: '';
            position: absolute;
            top: -50%; left: -50%;
            width: 200%; height: 200%;
            background: conic-gradient(
              from 0deg,
              transparent,
              rgba(16, 185, 129, 0.08),
              transparent,
              rgba(168, 85, 247, 0.06),
              transparent
            );
            animation: regSuccessRotate 5s linear infinite;
          }

          @keyframes regSuccessRotate {
            to { transform: rotate(360deg); }
          }

          .reg-success-card > * {
            position: relative;
            z-index: 2;
          }

          .reg-success-icon-wrap {
            width: 100px; height: 100px;
            border-radius: 50%;
            background: linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(52, 211, 153, 0.08));
            border: 3px solid rgba(16, 185, 129, 0.55);
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1.5rem;
            box-shadow:
              0 0 35px rgba(16, 185, 129, 0.35),
              inset 0 0 25px rgba(16, 185, 129, 0.1);
            animation: regIconPulse 2s ease-in-out infinite;
          }

          @keyframes regIconPulse {
            0%, 100% {
              box-shadow:
                0 0 35px rgba(16, 185, 129, 0.35),
                inset 0 0 25px rgba(16, 185, 129, 0.1);
            }
            50% {
              box-shadow:
                0 0 55px rgba(16, 185, 129, 0.55),
                inset 0 0 35px rgba(16, 185, 129, 0.2);
            }
          }

          .reg-success-icon-wrap i {
            color: #10b981;
            font-size: 2.8rem;
            filter: drop-shadow(0 0 12px rgba(16, 185, 129, 0.6));
          }

          .reg-success-title {
            font-size: 1.7rem;
            font-weight: 800;
            background: linear-gradient(90deg, #10b981, #34d399, #a855f7);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 0.7rem;
          }

          .reg-success-desc {
            color: #94a3b8;
            font-size: 1rem;
            line-height: 1.7;
            margin-bottom: 0.6rem;
          }

          .reg-success-name {
            color: #10b981;
            font-weight: 700;
            font-size: 1.1rem;
            text-shadow: 0 0 18px rgba(16, 185, 129, 0.4);
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }

          .reg-success-bar-track {
            width: 100%;
            height: 5px;
            background: rgba(255, 255, 255, 0.08);
            border-radius: 5px;
            margin-top: 2rem;
            overflow: hidden;
          }

          .reg-success-bar-fill {
            height: 100%;
            background: linear-gradient(90deg, #10b981, #a855f7);
            border-radius: 5px;
            animation: regBarCountdown 4.5s linear forwards;
            box-shadow: 0 0 12px rgba(16, 185, 129, 0.5);
          }

          @keyframes regBarCountdown {
            from { width: 100%; }
            to { width: 0%; }
          }

          .reg-success-redirect {
            color: #64748b;
            font-size: 0.82rem;
            margin-top: 0.9rem;
          }

          .reg-sparkle {
            position: absolute;
            width: 5px; height: 5px;
            border-radius: 50%;
            background: #10b981;
            animation: regSparkle 2.5s ease-in-out infinite;
          }
          .reg-sparkle:nth-child(1) { top: 12%; left: 8%; animation-delay: 0s; }
          .reg-sparkle:nth-child(2) { top: 18%; right: 12%; animation-delay: 0.6s; background: #a855f7; }
          .reg-sparkle:nth-child(3) { bottom: 22%; left: 18%; animation-delay: 1.2s; }
          .reg-sparkle:nth-child(4) { bottom: 12%; right: 8%; animation-delay: 1.8s; background: #00f9ff; }
          .reg-sparkle:nth-child(5) { top: 50%; left: 5%; animation-delay: 0.3s; background: #34d399; }

          @keyframes regSparkle {
            0%, 100% { opacity: 0; transform: scale(0) translateY(0); }
            50% { opacity: 1; transform: scale(1.2) translateY(-8px); }
          }

          /* ── Error Banner ── */
          .reg-error-banner {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(185, 28, 28, 0.08));
            border: 1px solid rgba(239, 68, 68, 0.4);
            border-radius: 14px;
            padding: 1rem 1.4rem;
            margin-bottom: 1.5rem;
            display: flex;
            align-items: center;
            gap: 0.8rem;
            box-shadow: 0 0 25px rgba(239, 68, 68, 0.12);
            animation: regErrorShake 0.5s ease-in-out;
          }

          @keyframes regErrorShake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-6px); }
            40% { transform: translateX(6px); }
            60% { transform: translateX(-4px); }
            80% { transform: translateX(4px); }
          }

          .reg-error-icon {
            color: #ef4444;
            font-size: 1.4rem;
            flex-shrink: 0;
            filter: drop-shadow(0 0 8px rgba(239, 68, 68, 0.5));
          }

          .reg-error-text {
            color: #fca5a5;
            font-size: 0.93rem;
            font-weight: 500;
            flex: 1;
          }

          .reg-error-close {
            background: none;
            border: none;
            color: #ef4444;
            font-size: 1.3rem;
            cursor: pointer;
            opacity: 0.6;
            transition: opacity 0.2s;
            padding: 0;
            line-height: 1;
          }

          .reg-error-close:hover {
            opacity: 1;
          }
        `}</style>
        <div className="cyber-bg-glow" />

        {/* ── Success Overlay ── */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              className="reg-success-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <motion.div
                className="reg-success-card"
                initial={{ scale: 0.4, opacity: 0, rotateX: 25 }}
                animate={{ scale: 1, opacity: 1, rotateX: 0 }}
                exit={{ scale: 0.85, opacity: 0, y: 25 }}
                transition={{ type: "spring", stiffness: 180, damping: 18, delay: 0.1 }}
              >
                <div className="reg-sparkle"></div>
                <div className="reg-sparkle"></div>
                <div className="reg-sparkle"></div>
                <div className="reg-sparkle"></div>
                <div className="reg-sparkle"></div>

                <motion.div
                  className="reg-success-icon-wrap"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.3 }}
                >
                  <i className="bi bi-check-circle-fill"></i>
                </motion.div>

                <motion.h2
                  className="reg-success-title"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  Byagenze Neza!
                </motion.h2>

                <motion.p
                  className="reg-success-desc"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.65 }}
                >
                  Umuyobozi w'Akagari yanditswe neza muri sisitemu.
                </motion.p>

                <motion.div
                  className="reg-success-name"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <i className="bi bi-person-badge"></i>
                  {successName}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                >
                  <div className="reg-success-bar-track">
                    <div className="reg-success-bar-fill"></div>
                  </div>
                  <p className="reg-success-redirect">
                    <i className="bi bi-arrow-repeat me-1"></i>
                    Uragiye woherezwa kuri lisiti y'Akagari...
                  </p>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="cyber-card">
          <h1 className="cyber-title">AKAGARI</h1>
          <p style={{ textAlign: 'center', color: cyberTheme.muted, marginBottom: '2rem' }}>
            Kwiyandikisha Umuyobozi w'Akagari
          </p>

          {/* ── Error Banner ── */}
          <AnimatePresence>
            {errorMessage && (
              <motion.div
                className="reg-error-banner"
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.3 }}
              >
                <i className="bi bi-exclamation-triangle-fill reg-error-icon"></i>
                <span className="reg-error-text">{errorMessage}</span>
                <button className="reg-error-close" onClick={() => setErrorMessage("")}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6 position-relative">
                <label className="label-cyber">Indangamuntu</label>
                <input name="national_id" className="input-cyber form-control" maxLength={16} value={formData.national_id} onChange={handleChange} />
                <i className="bi bi-fingerprint cyber-icon"></i>
                {errors.national_id && <div className="error-cyber">{errors.national_id}</div>}
              </div>
              <div className="col-md-6 position-relative">
                <label className="label-cyber">Amazina Yose</label>
                <input name="fullname" className="input-cyber form-control" value={formData.fullname} onChange={handleChange} />
                <i className="bi bi-person cyber-icon"></i>
                {errors.fullname && <div className="error-cyber">{errors.fullname}</div>}
              </div>
              <div className="col-md-6 position-relative">
                <label className="label-cyber">Imeyili</label>
                <input name="email" type="email" className="input-cyber form-control" value={formData.email} onChange={handleChange} />
                <i className="bi bi-envelope cyber-icon"></i>
                {errors.email && <div className="error-cyber">{errors.email}</div>}
              </div>
              <div className="col-md-6 position-relative">
                <label className="label-cyber">Telefone</label>
                <input name="telefone" className="input-cyber form-control" maxLength={15} value={formData.telefone} onChange={handleChange} />
                <i className="bi bi-telephone cyber-icon"></i>
                {errors.telefone && <div className="error-cyber">{errors.telefone}</div>}
              </div>
              <div className="col-md-6 position-relative">
                <label className="label-cyber">Ijambo ry'ibanga</label>
                <input name="password" type={showPassword ? "text" : "password"} className="input-cyber form-control" value={formData.password} onChange={handleChange} />
                <i className="bi bi-lock cyber-icon"></i>
                {errors.password && <div className="error-cyber">{errors.password}</div>}
              </div>
              <div className="col-md-6 position-relative">
                <label className="label-cyber">Emeza Ijambo</label>
                <input name="confirmPassword" type={showPassword ? "text" : "password"} className="input-cyber form-control" value={formData.confirmPassword} onChange={handleChange} />
                <i className="bi bi-shield-check cyber-icon"></i>
                {errors.confirmPassword && <div className="error-cyber">{errors.confirmPassword}</div>}
              </div>
            </div>
            <div className="mt-4 d-flex align-items-center">
              <input type="checkbox" id="showPass" checked={showPassword} onChange={() => setShowPassword(!showPassword)} style={{ accentColor: cyberTheme.primary }} />
              <label htmlFor="showPass" className="ms-2 text-light small">Erekana ijambo ry'ibanga</label>
            </div>
            <motion.button
              type="submit"
              className="btn-cyber"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              disabled={isLoading}
            >
              {isLoading ? "Tegereza..." : "EMEZA KWANDIKA"}
            </motion.button>
          </form>
        </div>
      </>
    );
  };

  return (
    <div className="superadmin-root" style={{ backgroundColor: cyberTheme.bg, minHeight: '100vh', minHeight: '100dvh', color: cyberTheme.text, display: 'flex', flexDirection: 'column' }}>
      <Navbar expand="lg" variant="dark" style={{ backgroundColor: '#1e293b' }}>
        <Container fluid>
          <Button
            variant="outline-info"
            className="d-lg-none me-2 superadmin-menu-btn"
            onClick={() => setShowMobileSidebar((prev) => !prev)}
            aria-label={showMobileSidebar ? 'Close dashboard navigation' : 'Open dashboard navigation'}
          >
            <i className={`bi ${showMobileSidebar ? 'bi-x-lg' : 'bi-list'}`}></i>
          </Button>
          <Navbar.Brand className="fw-bold fs-4">Super Admin</Navbar.Brand>
          <Navbar.Toggle />
          <Navbar.Collapse>
            <Nav className="me-auto">
              <Nav.Link active={activeKey === 'dashboard'} onClick={() => handleNavChange('dashboard')}>
                Dashboard
              </Nav.Link>
            </Nav>
            <Nav>
              <Nav.Link>Admin Profile</Nav.Link>
              <Nav.Link>Settings</Nav.Link>
              
              <Button 
                variant="outline-danger" 
                size="sm" 
                className="ms-2 d-flex align-items-center"
                style={{
                  border: '1px solid #ef4444',
                  boxShadow: '0 0 8px rgba(239, 68, 68, 0.4)',
                  color: '#ef4444'
                }}
              >
                <i className="bi bi-power me-1"></i> Sign Out
              </Button>

            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div
        className={`superadmin-sidebar-overlay ${showMobileSidebar ? 'show' : ''}`}
        aria-label="Close dashboard navigation"
        onClick={() => setShowMobileSidebar(false)}
        role="button"
        tabIndex={showMobileSidebar ? 0 : -1}
      />

      <div className="superadmin-layout d-flex">
        {/* Sidebar */}
        <div className={`superadmin-sidebar ${showMobileSidebar ? 'open' : ''}`} style={{
          width: '240px',
          backgroundColor: '#1e293b',
          height: 'calc(100vh - 56px)',
          position: 'fixed',
          borderRight: '1px solid #334155',
          paddingTop: '1.5rem',
          overflowY: 'auto',
        }}>
          <Nav className="flex-column px-3" variant="pills">
            <Nav.Link
              eventKey="dashboard"
              onClick={() => handleNavChange('dashboard')}
              className="py-3 text-white-50"
              active={activeKey === 'dashboard'}
            >
              <i className="bi bi-speedometer2 me-2"></i> Dashboard
            </Nav.Link>
            <hr className="my-3 border-secondary" />
            {['akagari', 'umudugudu', 'abandi', 'register'].map(key => (
              <Nav.Link
                key={key}
                eventKey={key}
                onClick={() => handleNavChange(key)}
                className="py-3 text-white-50"
                active={activeKey === key}
              >
                {key === 'register' ? 'Register Akagari' : key.charAt(0).toUpperCase() + key.slice(1)}
              </Nav.Link>
            ))}
            <Nav.Link
              eventKey="guhinduraUmuyobozi"
              onClick={() => handleNavChange('guhinduraUmuyobozi')}
              className="py-3 text-white-50"
              active={activeKey === 'guhinduraUmuyobozi'}
            >
              <i className="bi bi-person-gear me-2"></i>Guhindura Umuyobozi
            </Nav.Link>
            <Nav.Link
              eventKey="setPassword"
              onClick={() => handleNavChange('setPassword')}
              className="py-3 text-white-50"
              active={activeKey === 'setPassword'}
            >
              <i className="bi bi-shield-lock me-2"></i>Set Password
            </Nav.Link>

            <hr className="my-4 border-secondary" />

            <div className="text-uppercase text-muted small fw-bold px-3 mb-2">System Controls</div>
            
            <Button 
              variant="danger" 
              className="w-100 mb-3 mx-2" 
              style={{ 
                maxWidth: '220px',
                background: 'linear-gradient(45deg, #ef4444, #b91c1c)',
                border: 'none',
                boxShadow: '0 0 15px rgba(239, 68, 68, 0.5)',
                fontWeight: 'bold'
              }}
              onClick={() => alert("Logging out of system...")}
            >
              <i className="bi bi-box-arrow-right me-2"></i> Logout System
            </Button>

          </Nav>
        </div>

        {/* Main Content */}
        <div className="superadmin-main">
          <Container fluid>
            <Card className="mb-4 text-white" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
              <Card.Body>
                <h2 className="mb-1">Super Admin Panel</h2>
                <p className="mb-0 opacity-75">
                  {activeKey === 'dashboard' ? 'Dashboard Overview' : `Managing ${activeKey.charAt(0).toUpperCase() + activeKey.slice(1)}`} • {new Date().toLocaleDateString()}
                </p>
              </Card.Body>
            </Card>

            {renderContent()}
          </Container>
        </div>
      </div>

      <style>{`
        .cyber-calendar {
          background: rgba(15, 23, 42, 0.9) !important;
          color: #e2e8f0 !important;
          border: 1px solid rgba(99, 102, 241, 0.2) !important;
          border-radius: 12px !important;
          padding: 1rem !important;
          box-shadow: 0 0 25px rgba(99, 102, 241, 0.2) !important;
        }
        .cyber-calendar .react-calendar__navigation button {
          color: ${cyberTheme.primary} !important;
          background: transparent !important;
          border: none !important;
          font-weight: bold !important;
        }
        .cyber-calendar .react-calendar__navigation button:enabled:hover,
        .cyber-calendar .react-calendar__navigation button:enabled:focus {
          background: rgba(99, 102, 241, 0.12) !important;
          border-radius: 8px !important;
        }
        .cyber-calendar .react-calendar__month-view__weekdays__weekday abbr {
          text-decoration: none !important;
          color: ${cyberTheme.muted} !important;
          font-weight: 500 !important;
        }
        .cyber-calendar .react-calendar__tile {
          background: rgba(30, 41, 59, 0.6) !important;
          color: #e2e8f0 !important;
          border-radius: 8px !important;
          padding: 0.8em 0.3em !important;
          transition: all 0.2s ease !important;
        }
        .cyber-calendar .react-calendar__tile:enabled:hover,
        .cyber-calendar .react-calendar__tile:enabled:focus {
          background: rgba(99, 102, 241, 0.15) !important;
          color: ${cyberTheme.primary} !important;
          box-shadow: 0 0 15px rgba(99, 102, 241, 0.35) !important;
        }
        .cyber-calendar .today-tile {
          background: ${cyberTheme.primary}30 !important;
          color: ${cyberTheme.primary} !important;
          font-weight: bold !important;
          box-shadow: 0 0 15px rgba(99, 102, 241, 0.35) !important;
          border: 1px solid ${cyberTheme.primary}60 !important;
        }
        .cyber-calendar .react-calendar__tile--active {
          background: ${cyberTheme.primary} !important;
          color: #0a0e17 !important;
          font-weight: bold !important;
          box-shadow: 0 0 20px rgba(99, 102, 241, 0.6) !important;
        }
        .cyber-calendar .react-calendar__tile--neighboringMonth {
          color: ${cyberTheme.muted}80 !important;
        }
        .superadmin-layout {
          position: relative;
          width: 100%;
        }
        .superadmin-main {
          margin-left: 240px;
          width: calc(100% - 240px);
          padding: 1.5rem;
          min-width: 0;
          overflow-x: hidden;
        }
        .superadmin-menu-btn {
          min-width: 44px;
          min-height: 44px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .superadmin-sidebar {
          z-index: 1040;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .superadmin-sidebar-overlay {
          display: none;
          border: 0;
        }
        @media (max-width: 991.98px) {
          .superadmin-sidebar {
            width: min(86vw, 320px) !important;
            height: calc(100dvh - 56px) !important;
            transform: translateX(-100%);
            box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
          }
          .superadmin-sidebar.open {
            transform: translateX(0);
          }
          .superadmin-main {
            margin-left: 0 !important;
            width: 100% !important;
            padding: 1rem !important;
          }
          .superadmin-sidebar-overlay {
            display: block;
            position: fixed;
            inset: 56px 0 0 0;
            background: rgba(2, 6, 23, 0.55);
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.25s ease;
            z-index: 1035;
            border: 0;
            cursor: pointer;
          }
          .superadmin-sidebar-overlay.show {
            opacity: 1;
            pointer-events: auto;
          }
        }
        @media (max-width: 767.98px) {
          .superadmin-main {
            padding: 0.875rem !important;
          }
          .superadmin-layout .card-body {
            padding: 1rem !important;
          }
          .superadmin-layout .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .superadmin-layout .row.g-4 {
            --bs-gutter-x: 0.75rem;
            --bs-gutter-y: 0.75rem;
          }
        }
        @media (max-width: 575.98px) {
          .superadmin-main {
            padding: 0.75rem !important;
          }
          .superadmin-layout h2 {
            font-size: 1.25rem;
          }
          .superadmin-layout h4 {
            font-size: 0.95rem;
          }
          .superadmin-layout .btn {
            min-height: 44px;
          }
          .superadmin-layout .col-md-4 {
            flex: 0 0 100%;
            max-width: 100%;
          }
          .cyber-card {
            padding: 1.5rem 1rem !important;
            margin: 1rem 0 !important;
          }
          .cyber-title {
            font-size: 2.2rem !important;
          }
        }
      `}</style>
    </div>
  );
}

export default SuperAdminDashboard;